import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import {
  LinkToHotel,
  ImageCarousel,
  Rating,
  Location,
  Features,
  TripAdvisorRating,
  HotelPriceText,
  useAvailabilityHotelPrice,
  HomeFeatures,
  useHomeFeaturesFromHotel,
  HomeVacationRentalTag,
} from "@tenant/hotels";
import { Pill, PillTone } from "@tenant/ui";
import defaultStyles from "./HotelMapCard.module.scss";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { BonusEarnPill } from "@/modules/wallet";

export interface HotelMapCardComponentProps {
  hotel: Lodging;
  dateRange: DateRange;
  rooms?: number;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelMapCard;
export const HotelMapCard = ({
  hotel,
  dateRange,
  rooms,
}: HotelMapCardComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);

  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, locale } = useI18nContext();
  const { isExperimentVariant } = useExperiment();
  const hotelFundedOffersEnabled = isExperimentVariant(
    Experiments.HOTEL_FUNDED_OFFERS.name,
    Experiments.HOTEL_FUNDED_OFFERS.variants.available
  );

  const {
    context: {
      showPointsPricing,
      totalPoints,
      totalPrice,
      totalNights,
      percentOff,
    },
    text: { pricePerNight, totalPriceNotDiscounted },
  } = useAvailabilityHotelPrice(hotel, undefined, rooms, dateRange);

  const { isHomes, maxOccupancy, numberOfBedrooms, numberOfBathrooms } =
    useHomeFeaturesFromHotel(hotel);

  return (
    <div className={block}>
      <LinkToHotel hotel={hotel}>
        <div className={cn("carousel-container")}>
          <ImageCarousel
            className={cn("image-carousel")}
            imageUrlsArray={getImageUrlsArray(hotel.lodging.media)}
          />
          <BonusEarnPill variant="earn" className={cn("bonus-earn-pill")} />
        </div>
        <div className={cn("data-container")}>
          <div className={cn("pre-title-container")}>
            <Rating className={cn("rating")} hotel={hotel} />
            <Location className={cn("location")} city={hotel.lodging.city} />
            {isHomes ? (
              <span className={cn("entire-home")}>
                {t("core-hotels.homes.entireHome")}
              </span>
            ) : (
              <TripAdvisorRating
                className={cn("tripadvisor-rating")}
                hotel={hotel}
              />
            )}
          </div>
          {isHomes && (
            <HomeVacationRentalTag className={cn("vacation-rental-tag")} />
          )}
          <h2 className={cn("title")}>
            {htmlDecode(hotel.lodging.name) ?? hotel.lodging.name}
          </h2>
          <Features hotel={hotel} />
          {isHomes && (
            <HomeFeatures
              maxOccupancy={maxOccupancy}
              numberOfBedrooms={numberOfBedrooms}
              numberOfBathrooms={numberOfBathrooms}
              className={cn("homes-features")}
            />
          )}
          {hotelFundedOffersEnabled && percentOff > 0 ? (
            <Pill
              className={cn("discount-badge")}
              size="custom"
              tone={PillTone.custom}
            >
              {t("hotelCard.discountBadge", {
                percent: getPercentage(percentOff, locale),
              })}
            </Pill>
          ) : null}
          <HotelPriceText
            showPointsPricing={showPointsPricing}
            totalPoints={totalPoints}
            totalPrice={totalPrice}
            totalNights={totalNights}
            pricePerNightText={pricePerNight.final}
            totalPriceNotDiscounted={totalPriceNotDiscounted}
          />
        </div>
      </LinkToHotel>
    </div>
  );
};
